import { ADVANCED_FILTER_TYPE } from '../../constants';
import {
  AdvancedFeasibilityFilters,
  FeasibilityAdvancedFilterCriteriaType,
  FeasibilityFilterCriteria,
  FeasibilityFilterCriterion,
} from '../../models';
import { PsidRefDto } from '../../dtos/standard-survey.payload.dto';
import {
  AdvanceFeasibilityCriteriaDto,
  AdvanceFeasibilityCriteriasDto,
  FeasibilityExclusionsDto,
} from '../../dtos/feasibility-exclusion.dto';

const CRITERIA_ID_BY_TYPE: Record<
  FeasibilityAdvancedFilterCriteriaType,
  number
> = {
  projectManager: 1,
  billingNumber: 2,
  template: 3,
  surveyTitle: 4,
};

const CRITERIA_TYPE_BY_ID: Record<
  number,
  FeasibilityAdvancedFilterCriteriaType
> = {
  1: 'projectManager',
  2: 'billingNumber',
  3: 'template',
  4: 'surveyTitle',
};

const EXCLUDE_INCLUDE_TYPE = {
  [ADVANCED_FILTER_TYPE.INCLUDE]: 1,
  [ADVANCED_FILTER_TYPE.EXCLUDE]: 0,
};

/**
 * The survey fields a saved set of Advanced Filters is rebuilt from.
 *
 * Declared structurally rather than as a `Pick<SurveyDto>`, because `SurveyDto`
 * is the survey response contract and stays in `@ps/survey-data-access`.
 * `SurveyDto extends` this, so the compiler rejects any incompatible retyping
 * of these five fields.
 */
export interface SurveyFeasibilityDto {
  ps_survey_id: number;
  incl_excl?: number;
  survey_grouping?: number[];
  feasibility_exclusions?: FeasibilityExclusionsDto;
  ref_list?: PsidRefDto[];
}

export class SurveyFeasibilityMapper {
  /**
   * Maps a survey's feasibility data to Advanced Filters.
   *
   * The audience arrives split across two fields: `criteriaSurveys` is what the
   * criteria lookup matched, while `survey_grouping` is the whole audience —
   * so whatever is in the grouping but not in the criteria set was hand-picked.
   * The survey's own id appears in its grouping and is not a member of its own
   * audience, so it is dropped from both.
   */
  public mapToAdvancedFeasibilityFilters(
    dto: SurveyFeasibilityDto,
  ): AdvancedFeasibilityFilters {
    const exclusions = dto.feasibility_exclusions;

    return {
      type: dto.incl_excl ? 'include' : 'exclude',
      dateRange: {
        start: exclusions?.start ?? null,
        end: exclusions?.end ?? null,
      },
      criteria: this._mapFeasibilityCriteria(exclusions?.criterias ?? []),
      ...this._splitGroupIds(dto),
      respondentStatuses: exclusions?.statuses ?? [],
      excludeFromFeasibleAudience: !!exclusions?.enable,
      psidRefs: (dto.ref_list ?? []).map((ref) => ref.psid),
    };
  }

  /**
   * The whole feasibility audience, listed once each.
   * `feasibility_exclusions.criteriaSurveys` carries only the matched half.
   */
  public mapToSurveyGrouping(filters: AdvancedFeasibilityFilters): number[] {
    return [
      ...new Set([...filters.matchedGroupIds, ...filters.manualGroupIds]),
    ];
  }

  /** `1` includes the grouping in the audience, `0` excludes it. */
  public mapToInclExcl(filters: AdvancedFeasibilityFilters): number {
    return EXCLUDE_INCLUDE_TYPE[filters.type];
  }

  /** The PSIDs resolved for the Include-mode audience — empty otherwise. */
  public mapToRefList(filters: AdvancedFeasibilityFilters): PsidRefDto[] {
    return filters.psidRefs.map((psid) => ({ psid }));
  }

  /**
   * Maps applied advanced filters to a `FeasibilityExclusionsDto`.
   *
   * `statuses` is omitted when the filters carry none. Whether the buyer may
   * choose them at all is settled before the value is committed — see
   * `AdvancedFiltersStateService.canFilterRespondentStatus` — so there is
   * nothing left to gate here.
   */
  public mapToFeasibilityExclusions(
    filters: AdvancedFeasibilityFilters,
  ): FeasibilityExclusionsDto {
    return {
      enable: filters.excludeFromFeasibleAudience,
      start: filters.dateRange.start ?? '',
      end: filters.dateRange.end ?? '',
      criterias: filters.criteria.map((criteria) => ({
        id: CRITERIA_ID_BY_TYPE[criteria.type],
        name: criteria.name,
        values: criteria.values.map((value) =>
          this._criterionValue(criteria.type, value),
        ),
      })),
      criteriaSurveys: filters.matchedGroupIds,
      ...(filters.respondentStatuses.length
        ? { statuses: filters.respondentStatuses }
        : {}),
    };
  }

  private _splitGroupIds({
    ps_survey_id,
    feasibility_exclusions,
    survey_grouping,
  }: SurveyFeasibilityDto): Pick<
    AdvancedFeasibilityFilters,
    'matchedGroupIds' | 'manualGroupIds'
  > {
    const isOwnSurvey = (surveyId: number): boolean =>
      surveyId === ps_survey_id;

    const matchedGroupIds = (
      feasibility_exclusions?.criteriaSurveys ?? []
    ).filter((surveyId) => !isOwnSurvey(surveyId));

    return {
      matchedGroupIds,
      manualGroupIds: (survey_grouping ?? []).filter(
        (surveyId) =>
          !isOwnSurvey(surveyId) && !matchedGroupIds.includes(surveyId),
      ),
    };
  }

  private _mapFeasibilityCriteria(
    criterias: AdvanceFeasibilityCriteriasDto[],
  ): FeasibilityFilterCriteria[] {
    return criterias.map((criteria) => ({
      type: CRITERIA_TYPE_BY_ID[criteria.id],
      name: criteria.name,
      values: criteria.values.map((value) => ({
        // Billing Number values carry no id, so it arrives as the name.
        id: String(value.id ?? value.name),
        name: value.name,
      })),
    }));
  }

  /**
   * A Billing Number value is the billing id itself, so it goes out as a name
   * with no id. Every other criteria type is identified numerically.
   */
  private _criterionValue(
    type: FeasibilityAdvancedFilterCriteriaType,
    value: FeasibilityFilterCriterion,
  ): AdvanceFeasibilityCriteriaDto {
    if (type === 'billingNumber') {
      return { name: value.name };
    }

    return { id: Number(value.id), name: value.name };
  }
}
